// Seasonal collection DISPLAY helpers shared by web and mobile.
//
// Display only. Whether a collection is permanent / upcoming / active / ended,
// and whether its reward can still be earned, is decided by the backend
// (apps/backend/src/services/collectionAvailabilityService.js) and arrives on
// every collection as `availabilityStatus` and `canEarnReward`. Nothing here
// decides eligibility; it only turns the API's UTC dates into user-local copy.
//
// The window is half-open: availableFrom <= now < availableUntil. An end of
// "Nov 1, 00:00" therefore means "through Oct 31", so the last day shown is the
// day of the last earnable instant (availableUntil - 1 ms).

export const AVAILABILITY_STATUS = Object.freeze({
    PERMANENT: 'permanent',
    UPCOMING: 'upcoming',
    ACTIVE: 'active',
    ENDED: 'ended',
});

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(value) {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDay(date, { timeZone, locale, withYear = false } = {}) {
    try {
        return date.toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            ...(withYear ? { year: 'numeric' } : {}),
            ...(timeZone ? { timeZone } : {}),
        });
    } catch {
        return date.toDateString();
    }
}

/** The last instant the reward can be earned (end is exclusive). */
export function lastEarnableInstant(availableUntil) {
    const until = toDate(availableUntil);
    return until ? new Date(until.getTime() - 1) : null;
}

/** "Oct 5" — the first day, in the viewer's zone. */
export function formatSeasonStart(availableFrom, options) {
    const from = toDate(availableFrom);
    return from ? formatDay(from, options) : '';
}

/** "Oct 31" — the last earnable day, in the viewer's zone. */
export function formatSeasonEnd(availableUntil, options) {
    const last = lastEarnableInstant(availableUntil);
    return last ? formatDay(last, options) : '';
}

/** "Oct 5 – Oct 31" (inclusive days, viewer's zone). */
export function formatSeasonRange(availableFrom, availableUntil, options) {
    const start = formatSeasonStart(availableFrom, options);
    const end = formatSeasonEnd(availableUntil, options);
    if (!start || !end) return start || end;
    return start === end ? start : `${start} – ${end}`;
}

// Calendar-day difference in the viewer's zone (or `timeZone`), DST-safe.
function localDayNumber(date, timeZone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: 'numeric', day: 'numeric', ...(timeZone ? { timeZone } : {}),
        }).formatToParts(date);
        const get = (type) => Number(parts.find((p) => p.type === type)?.value);
        return Date.UTC(get('year'), get('month') - 1, get('day')) / DAY_MS;
    } catch {
        return Math.floor((date.getTime() - date.getTimezoneOffset() * 60000) / DAY_MS);
    }
}

/**
 * Countdown copy for an ACTIVE window: "Ends today", "Ends tomorrow",
 * "7 days left". Always shown next to the real date, never instead of it.
 */
export function seasonCountdownLabel(availableUntil, { now = new Date(), timeZone } = {}) {
    const last = lastEarnableInstant(availableUntil);
    if (!last) return '';
    const days = localDayNumber(last, timeZone) - localDayNumber(toDate(now), timeZone);
    if (days <= 0) return 'Ends today';
    if (days === 1) return 'Ends tomorrow';
    return `${days} days left`;
}

/**
 * Card / header badge for a collection as returned by the API. Returns null for
 * permanent collections so they render exactly as before.
 *   { status, label, detail }
 */
export function seasonalBadge(collection, options = {}) {
    const { availabilityStatus, availableFrom, availableUntil } = collection || {};
    // An earned limited edition is the user's history, not an expired challenge:
    // it reads LIMITED EDITION (with the label frozen on their coin) once the
    // season is over — or the collection has since been made permanent.
    const ownedLabel = collection?.rewardOwned ? collection.ownedSeasonLabel || null : null;
    if (collection?.rewardOwned && (ownedLabel || (collection.isSeasonal && availabilityStatus === AVAILABILITY_STATUS.ENDED))) {
        if (availabilityStatus !== AVAILABILITY_STATUS.ACTIVE && availabilityStatus !== AVAILABILITY_STATUS.UPCOMING) {
            return { status: 'owned', label: 'LIMITED EDITION', detail: ownedLabel || collection.seasonLabel || formatSeasonRange(availableFrom, availableUntil, options) };
        }
    }
    if (!collection?.isSeasonal) return null;
    switch (availabilityStatus) {
        case AVAILABILITY_STATUS.UPCOMING:
            return { status: availabilityStatus, label: 'COMING SOON', detail: `Starts ${formatSeasonStart(availableFrom, options)}` };
        case AVAILABILITY_STATUS.ACTIVE:
            return { status: availabilityStatus, label: 'LIMITED TIME', detail: formatSeasonRange(availableFrom, availableUntil, options) };
        case AVAILABILITY_STATUS.ENDED:
            return { status: availabilityStatus, label: 'SEASON ENDED', detail: formatSeasonRange(availableFrom, availableUntil, options) };
        default:
            return null;
    }
}

// ── Seasonal label on owned / shared coins ──────────────────────────────────
// An earned seasonal coin keeps its label forever (it is what makes
// "Halloween 2026" distinguishable from "Halloween 2027"), regardless of
// whether the season is still running. Accepts a collection or a coin's
// `collection` block; anything without a label (permanent collections, or a
// seasonal one the admin left unlabeled) yields null / the text unchanged.

function seasonLabelOf(source) {
    if (!source) return null;
    // A user's own coin: the label frozen when it was earned wins, even if the
    // collection was later relabelled or made permanent.
    const owned = typeof source.ownedSeasonLabel === 'string' ? source.ownedSeasonLabel.trim() : '';
    if (owned) return owned;
    const label = typeof source.seasonLabel === 'string' ? source.seasonLabel.trim() : '';
    return source.isSeasonal !== false && label ? label : null;
}

/** "Halloween 2026 · Limited edition", or null. */
export function seasonalEditionLabel(source) {
    const label = seasonLabelOf(source);
    return label ? `${label} · Limited edition` : null;
}

/** Appends " (Halloween 2026)" to share text for a labeled seasonal reward. */
export function withSeasonLabel(text, source) {
    const label = seasonLabelOf(source);
    if (!label || !text || text.includes(label)) return text;
    return `${text} (${label})`;
}
